import { useEffect, useState } from "react";
import SectionPanel from "pages/analytics/components/SectionPanel";
import {
  EmptyMessage,
  ErrorBanner,
  TableSkeleton,
} from "pages/analytics/components/AnalyticsStates";
import { formatCurrency, formatDate } from "pages/analytics/components/analyticsFormatters";
import { getUnattributedRevenue } from "services/marketing-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const REASON_LABELS = {
  no_contact_id: "No contact ID",
  no_matching_contact: "No matching contact",
  no_utm: "Missing UTM",
};

const REASON_STYLES = {
  no_contact_id: "bg-red-100 text-red-800",
  no_matching_contact: "bg-orange-100 text-orange-800",
  no_utm: "bg-yellow-100 text-yellow-800",
};

export default function UnattributedRevenueAlert({ api, filters }) {
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({ totalRevenue: 0, count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setSales([]);
      setSummary({ totalRevenue: 0, count: 0 });
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getUnattributedRevenue(api, { filters });
        if (!cancelled) {
          setSales(result?.sales ?? []);
          setSummary({
            totalRevenue: result?.totalRevenue ?? 0,
            count: result?.count ?? 0,
          });
        }
      } catch {
        if (!cancelled) {
          setSales([]);
          setSummary({ totalRevenue: 0, count: 0 });
          setError("Failed to load unattributed revenue. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const hasUnattributed = summary.count > 0;

  return (
    <SectionPanel title="Unattributed Revenue">
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {isLoading ? (
        <TableSkeleton rows={4} cols={4} />
      ) : isCustomRangeIncomplete(filters) ? (
        <EmptyMessage message="Select start and end dates to view unattributed revenue." />
      ) : error ? (
        <EmptyMessage message="Unattributed revenue unavailable due to a loading error." />
      ) : !hasUnattributed ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          All revenue is attributed for the selected period.
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 mb-4">
            <span className="font-medium">{summary.count} sale{summary.count !== 1 ? "s" : ""}</span>
            {" "}totaling{" "}
            <span className="font-medium">{formatCurrency(summary.totalRevenue)}</span>
            {" "}could not be attributed to a UTM source.
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Revenue
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sales.slice(0, 10).map((sale) => (
                  <tr key={sale.salesId} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-900">
                      {formatDate(sale.saleDate)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900">
                      {sale.productName || "-"}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 text-right">
                      {formatCurrency(sale.revenue)}
                    </td>
                    <td className="px-3 py-2.5 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          REASON_STYLES[sale.reason] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {REASON_LABELS[sale.reason] || sale.reason}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sales.length > 10 && (
              <p className="mt-3 text-xs text-gray-500">
                Showing 10 of {sales.length} unattributed sales.
              </p>
            )}
          </div>
        </>
      )}
    </SectionPanel>
  );
}
